import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { useTranslation } from 'react-i18next';
import { gql, useMutation } from '@apollo/client';

import useWithGA from '../hooks/useWithGA';
import useGetSeoSystem from '../hooks/data/useGetSeoSystem';
import useStrapiPageData from '../hooks/data/useStrapiPageData';
import useTransformGatsbyData from '../hooks/useTransformGatsbyData';
import useNavigation from '../hooks/useNavigation';
import useSearch from '../hooks/useSearch';

import { System } from '../ui/templates/system/System';

import MetatagsForLayout from '../components/MetatagsForLayout';

import { PAGE_INDEX } from '../constants/strapi';

import {
  getStructuredDataRating,
  removeHTMLAndLineBreak,
} from '../utils/utils';
import { getAverageReviewsRatings } from '../utils/reviews';

import { getLogoAssetObject } from '../utils/sharedFunc';
import useSystemPage from '../hooks/useSystemPage';

export const pageQuery = graphql`
  query (
    $category: String
    $categories: [String]
    $systemId: String
    $pageBuildId: String
    $sameCompanySystemsIds: [String]
    $systemsToCompareIds: [String]
  ) {
    categoryData: bwCategory(id: { eq: $category }) {
      data
    }
    categoryDefaultData: bwSinglePage(id: { eq: "category-default" }) {
      data
    }
    system: bwSystem(id: { eq: $systemId }) {
      data
    }
    sameCompanySystems: allBwSystem(
      filter: { id: { in: $sameCompanySystemsIds } }
    ) {
      nodes {
        data
      }
    }
    comparedSystems: allBwSystem(filter: { id: { in: $systemsToCompareIds } }) {
      nodes {
        data
      }
    }
    categoriesSystemProps: allBwSystemProp(
      filter: { category: { in: $categories } }
    ) {
      nodes {
        category
        dbName
        filter
      }
    }
    reviews: allBwReview(filter: { systemId: { eq: $systemId } }) {
      nodes {
        data
      }
    }
    site {
      siteMetadata {
        siteUrl
      }
    }
    bwPageBuildData(id: { eq: $pageBuildId }) {
      data
    }
  }
`;

const POST_SYSTEM_INQUIRY = gql`
  mutation CreateInquiry($data: String!) {
    createInquiry(data: $data) {
      result
    }
  }
`;

const SystemTemplate = ({ data, location, pageContext }) => {
  const bwPageBuildData = useTransformGatsbyData(data.bwPageBuildData);
  const { siteUrls, systemsToCompareData } = bwPageBuildData.pageData;

  const { menu, footer } = useNavigation(siteUrls, location);
  const search = useSearch();
  const { t } = useTranslation();

  const { trackOutboundLink, savePageView, handleDataLayer } = useWithGA();
  const { locale } = pageContext;

  const system = useTransformGatsbyData(data.system);
  const categoriesSystemProps = useTransformGatsbyData(
    data.categoriesSystemProps
  );
  const sameCompanySystems = useTransformGatsbyData(data.sameCompanySystems);
  const comparedSystems = useTransformGatsbyData(data.comparedSystems);
  const systemReviews = useTransformGatsbyData(data.reviews);

  useEffect(() => {
    savePageView({ type: 'SYSTEM', ids: system.id });
  }, []);

  const [
    createInquiryFn,
    { data: createInquiryData, loading: createInquiryLoading },
  ] = useMutation(POST_SYSTEM_INQUIRY);

  const { customer, breadcrumbs, sections } = useSystemPage(
    {
      system,
      categoriesSystemProps,
      sameCompanySystems,
      comparedSystems,
      systemReviews,
    },
    locale,
    systemsToCompareData,
    {
      createInquiryFn,
      createInquiryData,
      createInquiryLoading,
    }
  );

  return (
    <System
      isPaid={customer}
      breadcrumbs={breadcrumbs}
      sections={sections}
      t={t}
      locale={locale}
      menu={menu}
      footer={footer}
      siteUrls={siteUrls}
      location={location}
      search={search}
      handleDataLayer={handleDataLayer}
      trackOutboundLink={trackOutboundLink}
    />
  );
};

SystemTemplate.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  data: PropTypes.object.isRequired,
  pageContext: PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    data: PropTypes.object,
    id: PropTypes.string,
    isCreatedByStatefulCreatePages: PropTypes.bool,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
};

export default SystemTemplate;

function makeSystemSchema({ name, rating, ratings }) {
  // const desc = stripHTML(job.description)
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name,
    applicationCategory: 'BusinessApplication',
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: String(rating),
      ratingCount: String(ratings),
      bestRating: 5,
      worstRating: 0,
    },
  };
}

export function Head({ data, location, pageContext }) {
  const { locale } = pageContext;
  const { site } = data;
  const homePageData = useStrapiPageData(PAGE_INDEX)[1];

  const categoryData = useTransformGatsbyData(data.categoryData);
  const categoryDefaultData = useTransformGatsbyData(data.categoryDefaultData);
  const system = useTransformGatsbyData(data.system);
  const bwPageBuildData = useTransformGatsbyData(data.bwPageBuildData);
  const { siteUrls } = bwPageBuildData.pageData;
  const systemReviews = useTransformGatsbyData(data.reviews);

  const { ratingBW, systemSiteDataForLocale } = system;

  const averageRatings = getAverageReviewsRatings(systemReviews, locale);

  const ratingReviews =
    averageRatings.reduce(
      (acc, ratingQuestion) => acc + ratingQuestion.averageRating,
      0
    ) / averageRatings.length;

  const {
    systemName,
    companyName,
    metaDescription,
    introDescriptionWhat,
    introDescriptionWho,
    introDescription,
    systemDescription,
    customerType,
    mediaAssets,
  } = systemSiteDataForLocale;
  const { slugi18n: categoryUrl, categoryName, seo } = categoryData;

  const systemSeo = useGetSeoSystem(
    categoryData,
    categoryDefaultData,
    systemName,
    companyName
  );

  const { t } = useTranslation();
  const homeTextForBreadCrumb = t('common.home');
  const { seo: seoIndexPage } = homePageData;
  const introText = systemDescription || introDescription;
  const logoAsset = getLogoAssetObject(mediaAssets);

  const introDescriptionWhatNoHTML =
    removeHTMLAndLineBreak(introDescriptionWhat);
  const introDescriptionWhoNoHTML = removeHTMLAndLineBreak(introDescriptionWho);
  const introTextNoHTML = removeHTMLAndLineBreak(introText);

  const hasFaqTexts =
    !!introDescriptionWhat?.length || !!introDescriptionWho?.length;

  const mainEntities = [];

  if (hasFaqTexts) {
    if (introDescriptionWhatNoHTML) {
      mainEntities.push({
        '@type': 'Question',
        name: t('sp.descriptionTitle', { systemName }),
        acceptedAnswer: {
          '@type': 'Answer',
          text: introDescriptionWhatNoHTML,
        },
      });
    }
    if (introDescriptionWhoNoHTML) {
      mainEntities.push({
        '@type': 'Question',
        name: t('sp.descriptionTitle2', { systemName }),
        acceptedAnswer: {
          '@type': 'Answer',
          text: introDescriptionWhoNoHTML,
        },
      });
    }
  } else {
    mainEntities.push({
      '@type': 'Question',
      name: t('sp.descriptionTitle', { systemName }),
      acceptedAnswer: {
        '@type': 'Answer',
        text: introTextNoHTML,
      },
    });
  }

  const faqDataBlock = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [...mainEntities],
  });

  const breadcrumbDataBlock = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: homeTextForBreadCrumb,
        item: {
          '@id': site.siteMetadata.siteUrl,
          name: seoIndexPage.metaTitle,
          description: seoIndexPage.metaDescription,
          url: site.siteMetadata.siteUrl,
          // sameAs: [
          //   'https://businesswith.se',
          //   'https://businesswith.no',
          //   'https://businesswith.dk',
          // ],
        },
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: categoryName,
        item: {
          '@id': `${site.siteMetadata.siteUrl}/${categoryUrl}/`,
          name: categoryName,
          description: seo.metaDescription,
          url: `${site.siteMetadata.siteUrl}/${categoryUrl}/`,
        },
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: systemName,
        item: {
          '@id': site.siteMetadata.siteUrl + location.pathname,
          name: systemName,
          description: systemSeo.metaDescription || metaDescription,
          url: site.siteMetadata.siteUrl + location.pathname,
        },
      },
    ],
  };

  let structuredDataRating = {};
  if ((ratingBW || ratingReviews) && systemReviews.length > 0) {
    const structuredRating = getStructuredDataRating(
      ratingBW,
      ratingReviews,
      customerType
    );

    if (structuredRating > 0) {
      structuredDataRating = JSON.stringify(
        makeSystemSchema({
          name: systemName,
          rating: structuredRating,
          ratings: systemReviews.length,
        })
      );
    }
  }

  return (
    <>
      <MetatagsForLayout
        title={systemSeo.metaTitle}
        description={systemSeo.metaDescription}
        thumbnail={`https://ik.imagekit.io/businesswith/tr:w-200,h-100,cm-pad_resize,dpr-2/logo/${logoAsset.url}`}
        pathname={location.pathname}
        locale={locale}
        siteUrls={siteUrls}
      />
      <script type="application/ld+json">{faqDataBlock}</script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbDataBlock)}
      </script>
      {Object.keys(structuredDataRating).length && (
        <script type="application/ld+json">{structuredDataRating}</script>
      )}
    </>
  );
}
